import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import {
  firebaseCreateUser, firebaseSendPasswordReset, firebaseSignInWithPassword, firebaseSignOut,
  getFirebaseAuth, subscribeAuth,
} from "./firebase";
import { setAuthSession } from "./session";

export type AuthState = {
  status: "loading" | "signedOut" | "signedIn";
  user: { uid: string; email: string | null } | null;
  getIdToken(force?: boolean): Promise<string>;
  signIn(email: string, password: string): Promise<void>;
  signUp(email: string, password: string): Promise<void>;
  signOut(): Promise<void>;
  sendPasswordReset(email: string): Promise<void>;
};

const AuthContext = createContext<AuthState | null>(null);

// Module-level and therefore stable: both close over the Firebase SDK only, never over React
// state. That is what lets the module session below be published straight from the
// onAuthStateChanged callback, with no dependency on a render having happened.
const getIdToken = async (force?: boolean): Promise<string> => {
  const current = getFirebaseAuth().currentUser;
  if (!current) throw new Error("Cannot get an ID token while signed out.");
  return current.getIdToken(force);
};

const doSignOut = async (): Promise<void> => {
  await firebaseSignOut();
};

export function AuthProvider({ children }: { children: ReactNode }) {
  const [snapshot, setSnapshot] = useState<{
    status: AuthState["status"]; uid: string | null; email: string | null;
  }>({ status: "loading", uid: null, email: null });

  useEffect(
    () => subscribeAuth((u) => {
      // Publish the module session SYNCHRONOUSLY, here inside the Firebase callback — this
      // must NOT move back into a useEffect keyed on the snapshot.
      //
      // api.ts's send() reads the bearer token via getAuthSession() on every call, and
      // InviteScreen calls api.acceptInvite() in the microtask right after auth.signUp()
      // resolves. If publishing waited for React to flush an effect, that call could go out
      // with no Authorization header; POST /auth/staff/invites/accept is [Authorize], so it
      // would 401 and the invitee would be told their perfectly good invitation is "invalid,
      // expired, or already used". Setting it here removes React's scheduling from the path.
      setAuthSession(u ? { uid: u.uid, getIdToken, signOut: doSignOut } : null);
      setSnapshot({ status: u ? "signedIn" : "signedOut", uid: u?.uid ?? null, email: u?.email ?? null });
    }),
    [],
  );

  const value = useMemo<AuthState>(() => ({
    status: snapshot.status,
    user: snapshot.uid ? { uid: snapshot.uid, email: snapshot.email } : null,
    getIdToken,
    async signIn(email: string, password: string) {
      await firebaseSignInWithPassword(email, password);
    },
    async signUp(email: string, password: string) {
      await firebaseCreateUser(email, password);
    },
    signOut: doSignOut,
    async sendPasswordReset(email: string) {
      await firebaseSendPasswordReset(email);
    },
  }), [snapshot]);

  // (The auth half of the module session api.ts reads — see auth/session.ts for why it is a
  // module and not a context — is published in the subscribeAuth callback above, not from an
  // effect here. The scope half is still set by ScopeGate once /auth/staff/me resolves.)

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside AuthProvider");
  return ctx;
}
